import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SmartBriefAnalytics, SmartBriefSharing } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

/**  @deprecated Should use types directly from the API */
export type SmartBrief = {
  id: string;
  merchantId: string;
  batchId: string;
  productName: string;
  title: string;
  status: "draft" | "generating" | "completed";
  shareId?: string;
  isPublic: boolean;
  isPasswordProtected: boolean;
  sharePassword?: string;
  sharedAt?: string;
  bannerColor?: string;
  logoUrl?: string;
  productThumbnail?: string;
  createdAt: string;
  updatedAt: string;
};

/** Category item in do_list/dont_list */
export type BriefCategoryItem = {
  category_header: string;
  description: string;
  key_patterns: string[];
};

/**
 * Unified BriefContent type supporting both legacy (pre-cold) and new (cold) brief schemas.
 *
 * Schema differences:
 * - Legacy: has `brief` field (prose), `good examples` (with space), no `safetyList`, `metric` = "revenue"
 * - New: has `safetyList`, `good_examples` (underscore), no `brief` field, `metric` = "revenue_per_view"
 */
export type BriefContent = {
  product: string;
  metric: string; // "revenue" (legacy) or "revenue_per_view" (new)
  audience: string;
  tone: string;
  // Legacy - full prose brief (empty string for new cold briefs)
  brief: string;
  // New only - newline-separated safety rules
  safetyList?: string;
  doList: BriefCategoryItem[];
  dontList: BriefCategoryItem[];
  usage: {
    pattern: { input_tokens: number; output_tokens: number } | string | null;
    summary: { input_tokens: number; output_tokens: number } | string | null;
    brief: { input_tokens: number; output_tokens: number } | string | null;
  };
  // Normalized to goodExamples, but raw data might have "good examples" (with space)
  goodExamples: string[];
};

/**
 * Default empty brief content structure for creating new briefs.
 * Used when no generated content is available.
 */
export const EMPTY_BRIEF_CONTENT: BriefContent = {
  product: "",
  metric: "revenue_per_view",
  audience: "",
  tone: "",
  brief: "",
  safetyList: "",
  doList: [
    { category_header: "Hook", description: "", key_patterns: [] },
    { category_header: "Video Style (Archetypes)", description: "", key_patterns: [] },
    { category_header: "Selling Points", description: "", key_patterns: [] },
    { category_header: "Sales Tactics (Tactics)", description: "", key_patterns: [] },
  ],
  dontList: [
    { category_header: "Hook", description: "", key_patterns: [] },
    { category_header: "Video Style (Archetypes)", description: "", key_patterns: [] },
    { category_header: "Selling Points", description: "", key_patterns: [] },
    { category_header: "Sales Tactics (Tactics)", description: "", key_patterns: [] },
  ],
  usage: { pattern: null, summary: null, brief: null },
  goodExamples: [],
};

/**
 * Normalizes raw brief content from API to unified BriefContent type.
 * Handles both legacy (pre-cold) and new (cold) brief schemas.
 */
export function normalizeBriefContent(raw: Record<string, unknown>): BriefContent {
  // Handle "good examples" (legacy with space) vs "good_examples" (new with underscore)
  const goodExamples = (raw.good_examples ?? raw["good examples"] ?? raw.goodExamples ?? []) as string[];

  return {
    product: (raw.product as string) || "",
    metric: (raw.metric as string) || "revenue_per_view",
    audience: (raw.audience as string) || "",
    tone: (raw.tone as string) || "",
    brief: (raw.brief as string) || "",
    safetyList: (raw.safetyList ?? raw.safety_list) as string | undefined,
    doList: ((raw.do_list ?? raw.doList) as BriefCategoryItem[]) || [],
    dontList: ((raw.dont_list ?? raw.dontList) as BriefCategoryItem[]) || [],
    usage: (raw.usage as BriefContent["usage"]) || { pattern: null, summary: null, brief: null },
    goodExamples,
  };
}

// React Query hooks
export function useSmartBriefs() {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      if (!scope.merchantId) return [];
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["smart-briefs"].$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
}

export function useSmartBrief(briefId?: string) {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-brief", scope.merchantId, scope.workspaceId, briefId],
    queryFn: async () => {
      if (!scope.merchantId || !briefId) return null;
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].$get({
            param: {
              ...scope.param,
              briefId: briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].$get({
            param: {
              ...scope.param,
              briefId: briefId,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!briefId,
  });
}

export function useSmartBriefContent(briefId?: string, productName?: string) {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId, productName],
    queryFn: async () => {
      if (!scope.merchantId || !briefId || !productName) return null;
      const response = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].content.$get({
            param: {
              ...scope.param,
              briefId,
            },
            query: {
              productName,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].content.$get({
            param: {
              ...scope.param,
              briefId,
            },
            query: {
              productName,
            },
          });

      if (!response.ok) {
        const errorText = await response.text();

        // If content not found or not generated yet, return brief info without content (for new products)
        if (
          response.status === 404 ||
          errorText.includes("Brief content not generated yet") ||
          errorText.includes("content not found")
        ) {
          // Fetch just the brief metadata
          const briefResponse = scope.workspaceId
            ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].$get({
                param: {
                  ...scope.param,
                  briefId: briefId,
                },
              })
            : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].$get({
                param: {
                  ...scope.param,
                  briefId: briefId,
                },
              });

          if (briefResponse.ok) {
            const brief = await briefResponse.json();
            return { brief, content: null, examples: [] };
          }
        }

        throw new Error(`Failed to fetch brief content (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      return data;
    },
    enabled: !!scope.merchantId && !!briefId && !!productName,
    retry: false, // Don't retry on error to prevent flashing
    refetchOnWindowFocus: false, // Prevent refetching when window gains focus
  });
}

/**
 * Preview/generate brief content without saving to DB.
 * Used for draft mode - content is only persisted when user explicitly saves.
 */
export function usePreviewSmartBrief() {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (data: { productName: string }) => {
      if (!scope.merchantId) throw new Error("No merchant available");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"].preview.$post({
            param: scope.param,
            json: data,
          })
        : await client.merchant[":merchantId"]["smart-briefs"].preview.$post({
            param: scope.param,
            json: data,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
}

/**
 * Create a brief with content in a single atomic operation.
 * Used when saving a draft - creates the brief record with content in one transaction.
 */
export function useCreateSmartBriefWithContent() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      productName: string;
      title: string;
      content: BriefContent;
      productThumbnail?: string;
    }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      // Create brief with content in a single transaction
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"]["with-content"].$post({
            param: scope.param,
            json: {
              productName: data.productName,
              title: data.title,
              productThumbnail: data.productThumbnail,
              content: data.content as unknown as Parameters<
                (typeof client.merchant)[":merchantId"]["smart-briefs"]["with-content"]["$post"]
              >[0]["json"]["content"],
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"]["with-content"].$post({
            param: scope.param,
            json: {
              productName: data.productName,
              title: data.title,
              productThumbnail: data.productThumbnail,
              content: data.content as unknown as Parameters<
                (typeof client.merchant)[":merchantId"]["smart-briefs"]["with-content"]["$post"]
              >[0]["json"]["content"],
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useDuplicateSmartBrief() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (briefId: string) => {
      if (!scope.merchantId) throw new Error("No merchant available");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].duplicate.$post({
            param: {
              ...scope.param,
              briefId: briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].duplicate.$post({
            param: {
              ...scope.param,
              briefId: briefId,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useDeleteSmartBrief() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (briefId: string) => {
      if (!scope.merchantId) throw new Error("No merchant available");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].$delete({
            param: {
              ...scope.param,
              briefId: briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].$delete({
            param: {
              ...scope.param,
              briefId: briefId,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useUpdateSmartBriefContent() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      productName,
      content,
    }: {
      briefId: string;
      productName: string;
      content: BriefContent;
    }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      // Cast content to match API expectations
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].content.$put({
            param: {
              ...scope.param,
              briefId,
            },
            json: content as unknown as Parameters<
              (typeof client.merchant)[":merchantId"]["smart-briefs"][":briefId"]["content"]["$put"]
            >[0]["json"],
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].content.$put({
            param: {
              ...scope.param,
              briefId,
            },
            json: content as unknown as Parameters<
              (typeof client.merchant)[":merchantId"]["smart-briefs"][":briefId"]["content"]["$put"]
            >[0]["json"],
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { briefId, productName }) => {
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["smart-brief", scope.merchantId, scope.workspaceId, briefId] });
      queryClient.invalidateQueries({
        queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId, productName],
      });
    },
  });
}

/**
 * Update brief metadata (title, bannerColor, productThumbnail).
 * Consolidated hook replacing useUpdateSmartBriefTitle and useUpdateSmartBriefBannerColor.
 */
export function useUpdateSmartBrief() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      title,
      bannerColor,
      productThumbnail,
    }: {
      briefId: string;
      title?: string;
      bannerColor?: string;
      productThumbnail?: string | null; // null to clear, string to set
    }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].$patch({
            param: {
              ...scope.param,
              briefId,
            },
            json: { title, bannerColor, productThumbnail },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].$patch({
            param: {
              ...scope.param,
              briefId,
            },
            json: { title, bannerColor, productThumbnail },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { briefId }) => {
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["smart-brief", scope.merchantId, scope.workspaceId, briefId] });
      queryClient.invalidateQueries({
        queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId],
      });
    },
  });
}

// Sharing hooks
export function useSmartBriefSharing(briefId?: string) {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-brief-sharing", scope.merchantId, scope.workspaceId, briefId],
    queryFn: async (): Promise<SmartBriefSharing> => {
      if (!scope.merchantId || !briefId) throw new Error("Missing required parameters");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].sharing.$get({
            param: {
              ...scope.param,
              briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].sharing.$get({
            param: {
              ...scope.param,
              briefId,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!briefId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSmartBriefSharing() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      isPublic,
      isPasswordProtected,
      password,
    }: {
      briefId: string;
      isPublic: boolean;
      isPasswordProtected?: boolean;
      password?: string;
    }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].sharing.$put({
            param: {
              ...scope.param,
              briefId,
            },
            json: {
              isPublic,
              isPasswordProtected,
              password,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].sharing.$put({
            param: {
              ...scope.param,
              briefId,
            },
            json: {
              isPublic,
              isPasswordProtected,
              password,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: async (_, { briefId }) => {
      // Refetch sharing data explicitly to ensure UI updates with new share URL
      await queryClient.refetchQueries({
        queryKey: ["smart-brief-sharing", scope.merchantId, scope.workspaceId, briefId],
      });
      await queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useUploadSmartBriefLogo() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ briefId, logoFile }: { briefId: string; logoFile: File }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].logo.$post({
            param: {
              ...scope.param,
              briefId,
            },
            form: {
              logo: logoFile,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].logo.$post({
            param: {
              ...scope.param,
              briefId,
            },
            form: {
              logo: logoFile,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { briefId }) => {
      queryClient.invalidateQueries({
        queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId],
      });
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useDeleteSmartBriefLogo() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ briefId }: { briefId: string }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].logo.$delete({
            param: {
              ...scope.param,
              briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].logo.$delete({
            param: {
              ...scope.param,
              briefId,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { briefId }) => {
      queryClient.invalidateQueries({
        queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId],
      });
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useUploadSmartBriefProductThumbnail() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ briefId, thumbnailFile }: { briefId: string; thumbnailFile: File }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"][
            "product-thumbnail"
          ].$post({
            param: {
              ...scope.param,
              briefId,
            },
            form: {
              thumbnail: thumbnailFile,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"]["product-thumbnail"].$post({
            param: {
              ...scope.param,
              briefId,
            },
            form: {
              thumbnail: thumbnailFile,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { briefId }) => {
      queryClient.invalidateQueries({
        queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId],
      });
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

export function useDeleteSmartBriefProductThumbnail() {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ briefId }: { briefId: string }) => {
      if (!scope.merchantId) throw new Error("No merchant available");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"][
            "product-thumbnail"
          ].$delete({
            param: {
              ...scope.param,
              briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"]["product-thumbnail"].$delete({
            param: {
              ...scope.param,
              briefId,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, { briefId }) => {
      queryClient.invalidateQueries({
        queryKey: ["smart-brief-content", scope.merchantId, scope.workspaceId, briefId],
      });
      queryClient.invalidateQueries({ queryKey: ["smart-briefs", scope.merchantId, scope.workspaceId] });
    },
  });
}

// Analytics hooks
export function useSmartBriefAnalytics(briefId?: string) {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-brief-analytics", scope.merchantId, scope.workspaceId, briefId],
    queryFn: async (): Promise<SmartBriefAnalytics> => {
      if (!scope.merchantId || !briefId) throw new Error("Missing required parameters");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"][":briefId"].analytics.$get({
            param: {
              ...scope.param,
              briefId,
            },
          })
        : await client.merchant[":merchantId"]["smart-briefs"][":briefId"].analytics.$get({
            param: {
              ...scope.param,
              briefId,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!briefId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export function useSmartBriefViewCounts() {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-brief-view-counts", scope.merchantId, scope.workspaceId],
    queryFn: async (): Promise<Record<string, number>> => {
      if (!scope.merchantId) return {};

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-briefs"]["view-counts"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["smart-briefs"]["view-counts"].$get({
            param: scope.param,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
